import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Button, CircularProgress, Collapse, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { CopyButton, ErrorAlert, formatTs, mono } from '../components';
import type { Integration, IntegrationAccount, IntegrationWebhookToken, WebhookEndpoint } from '../integrationTypes';
import { useAction, useIntegrations } from './IntegrationsContext';
import { ago, credentialsReady, setupHelp, WEBHOOK_TYPES } from './integrationLabels';
import { AccountName, CardSection } from './parts';
import { GuideValue, guideFor, SetupGuide } from './setupGuides';

const code = { ...mono, bgcolor: 'action.hover', borderRadius: 0.5, px: 0.75, py: 0.25 };

/** The token, hidden until "Show" (plan §1.5): each answer is audited server-side; the value is never logged,
 *  and "Hide" forgets it. */
const TokenRow = ({ connection, account }: { connection: Integration; account: IntegrationAccount }) => {
    const { api, permissions } = useIntegrations();
    const [token, setToken] = React.useState<IntegrationWebhookToken | null>(null);
    const action = useAction();
    // A change of the account (a save answers a new connection: credentials or token may have changed) hides it.
    React.useEffect(() => {
        setToken(null);
    }, [account]);

    if (!account.webhooks?.token_available) {
        return null;
    }
    if (!permissions.view_token) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="token-hidden">
                A webhook token is set. Only users who may change integrations or company settings can show it.
            </Typography>
        );
    }
    const show = async () => {
        const outcome = await action.run(() => api.webhookToken(connection.id, account.slug));
        if (outcome.ok) {
            setToken(outcome.value);
        }
    };
    return (
        <Box sx={{ mt: 1 }} data-testid="token-row">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '104px minmax(0, 1fr)' }, columnGap: 1.5, rowGap: 0.5, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Token</Typography>
                {token ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box component="code" sx={{ ...code, minWidth: 0 }} data-testid="token-value">{`${token.header.name}: ${token.header.value}`}</Box>
                        <CopyButton value={token.header.value} label="token header value" />
                        <Button size="small" startIcon={<VisibilityOffIcon fontSize="small" />} onClick={() => setToken(null)}>Hide</Button>
                    </Stack>
                ) : (
                    <Box>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={action.busy ? <CircularProgress size={14} /> : <VisibilityIcon fontSize="small" />}
                            disabled={action.busy}
                            onClick={show}
                        >
                            Show token
                        </Button>
                    </Box>
                )}
            </Box>
            {token && (
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5, mb: 0 }}>
                    Keep it private: it lets {connection.name} send bookings to your account. Showing it is recorded in the Activity.
                </Typography>
            )}
            {action.error ? <ErrorAlert error={action.error} /> : null}
        </Box>
    );
};

const InstallButton = ({ connection, account }: { connection: Integration; account: IntegrationAccount }) => {
    const { api, replace } = useIntegrations();
    const { enqueueSnackbar } = useSnackbar();
    const action = useAction();
    const install = async () => {
        const outcome = await action.run(() => api.installWebhooks(connection.id, account.slug));
        if (outcome.ok) {
            replace(outcome.value);
            enqueueSnackbar(`Webhooks installed at ${connection.name}`, { variant: 'success' });
        }
    };
    return (
        <Box sx={{ mt: 1, ml: { sm: '116px' } }}>
            <Button size="small" variant="outlined" onClick={install} disabled={action.busy} startIcon={action.busy ? <CircularProgress size={14} /> : undefined}>
                Install webhooks
            </Button>
            {action.error ? <ErrorAlert error={action.error} /> : null}
        </Box>
    );
};

/** One receive address: its type, the URL with copy, and the last request (`last` undefined: not shown). */
const EndpointRow = ({ endpoint, last, testId = 'endpoint-url' }: { endpoint: WebhookEndpoint; last?: string | null; testId?: string }) => (
    <Box
        component="li"
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '104px minmax(0, 1fr)' }, columnGap: 1.5, rowGap: 0.25 }}
    >
        <Typography variant="body2" color="text.secondary" sx={{ pt: { sm: 0.25 } }}>{WEBHOOK_TYPES[endpoint.type] || endpoint.type}</Typography>
        <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Box component="code" sx={{ ...code, minWidth: 0 }} data-testid={testId}>{endpoint.url}</Box>
                <CopyButton value={endpoint.url} label={`${(WEBHOOK_TYPES[endpoint.type] || endpoint.type).toLowerCase()} webhook address`} />
            </Stack>
            {last !== undefined && (
                <Typography variant="caption" color="text.secondary" title={last ? formatTs(last) : undefined}>
                    {last ? `Last request ${ago(last)}` : 'No request yet'}
                </Typography>
            )}
        </Box>
    </Box>
);

const AccountEndpoints = ({ connection, account, titled, canInstall }: {
    connection: Integration; account: IntegrationAccount; titled: boolean; canInstall: boolean;
}) => {
    const hooks = account.webhooks;
    if (!hooks) {
        return null;
    }
    return (
        <Box sx={{ '& + &': { mt: 2 } }} data-testid={`endpoints-${account.slug || 'default'}`}>
            {titled && <Box sx={{ mb: 0.5 }}><AccountName name={account.display_name} slug={account.slug} /></Box>}
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', rowGap: 1 }}>
                {hooks.endpoints.map((endpoint) => (
                    <EndpointRow key={endpoint.type} endpoint={endpoint} last={hooks.last_request_at?.[endpoint.type] ?? null} />
                ))}
            </Box>
            <TokenRow connection={connection} account={account} />
            {canInstall && account.is_default && <InstallButton connection={connection} account={account} />}
            {canInstall && !account.is_default && (
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, mb: 0, maxWidth: '68ch' }} data-testid="install-default-only">
                    Install webhooks covers the Default account only: add this account&apos;s addresses in {connection.name} yourself.
                </Typography>
            )}
        </Box>
    );
};

/** `**x**` in guide text, bold. */
const Rich = ({ text }: { text: string }) => (
    <>
        {text.split(/(\*\*[^*]+\*\*)/).map((part, idx) => (
            // The parts of one fixed sentence never reorder.
            part.startsWith('**') && part.endsWith('**') ? <strong key={idx}>{part.slice(2, -2)}</strong> : <React.Fragment key={idx}>{part}</React.Fragment>
        ))}
    </>
);

/** The vendor's own dashboard, step by step (the former Settings › Integrations guides, review I5 #2), filled in
 *  with the chosen account's real addresses and its token. Collapsed until asked for. */
const SetupGuideBlock = ({ connection, guide, accounts }: { connection: Integration; guide: SetupGuide; accounts: IntegrationAccount[] }) => {
    const [open, setOpen] = React.useState(false);
    const [picked, setPicked] = React.useState(accounts[0]?.slug || '_default');
    const account = accounts.find((a) => (a.slug || '_default') === picked) || accounts[0];
    const endpoints = account?.webhooks?.endpoints || [];
    const panelId = `setup-guide-${connection.id}`;
    const valueOf = (value: GuideValue, idx: number) => {
        if (value === 'token') {
            return <TokenRow key={`token-${idx}`} connection={connection} account={account} />;
        }
        const endpoint = endpoints.find((e) => e.type === value.url) || endpoints[0];
        return endpoint ? (
            <Box component="ul" key={`url-${idx}`} sx={{ listStyle: 'none', m: 0, mt: 1, p: 0 }}>
                <EndpointRow endpoint={endpoint} testId="guide-url" />
            </Box>
        ) : null;
    };
    return (
        <Box sx={{ mt: 2 }} data-testid="setup-guide">
            <Button
                size="small"
                aria-expanded={open}
                aria-controls={panelId}
                startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setOpen((o) => !o)}
            >
                Set it up in {connection.name}, step by step
            </Button>
            <Collapse in={open} unmountOnExit>
                <Box id={panelId} sx={{ mt: 1, maxWidth: '75ch' }}>
                    {accounts.length > 1 && (
                        <TextField
                            select
                            size="small"
                            label="Addresses of"
                            value={picked}
                            onChange={(ev) => setPicked(ev.target.value)}
                            SelectProps={{ MenuProps: { sx: { zIndex: 10002 } } }}
                            inputProps={{ 'data-testid': 'guide-account' }}
                            sx={{ mb: 1.5, minWidth: 220 }}
                        >
                            {accounts.map((a) => <MenuItem key={a.slug || '_default'} value={a.slug || '_default'}>{a.display_name}</MenuItem>)}
                        </TextField>
                    )}
                    <Typography variant="body2" sx={{ mb: 1.5 }}><Rich text={guide.intro} /></Typography>
                    <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'grid', rowGap: 2 }}>
                        {guide.steps.map((step) => (
                            <Box component="li" key={step.title} sx={{ typography: 'body2' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{step.title}</Typography>
                                {step.text && <Typography variant="body2"><Rich text={step.text} /></Typography>}
                                {step.list && (
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {step.list.map((item) => <li key={item}><Rich text={item} /></li>)}
                                    </Box>
                                )}
                                {step.after && <Typography variant="body2"><Rich text={step.after} /></Typography>}
                                {account && (step.values || []).map(valueOf)}
                                {step.image && (
                                    <Box sx={{ mt: 1 }}>
                                        <Box
                                            component="a"
                                            href={step.image.src}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Open the full-size screenshot"
                                            sx={{ display: 'inline-block', lineHeight: 0, borderRadius: 1, '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: '2px' } }}
                                        >
                                            <Box
                                                component="img"
                                                src={step.image.src}
                                                alt={step.image.alt}
                                                loading="lazy"
                                                // A thumbnail inside 360 x 300, aspect kept (some are tall portraits); the link opens it full size.
                                                sx={{ width: 'auto', height: 'auto', maxWidth: 'min(360px, 100%)', maxHeight: 300, border: 1, borderColor: 'divider', borderRadius: 1 }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

/** Where the booking system sends its webhooks, per account and type (plan §1.5), with the set-up its
 *  `setup_mode` asks for. Ventrata's [Install webhooks] shows in production only (`install.available`). */
const WebhookEndpoints = ({ connection, canEdit }: { connection: Integration; canEdit: boolean }) => {
    const { environment } = useIntegrations();
    const hooks = connection.webhooks;
    const accounts = connection.accounts.filter((a) => a.webhooks && a.webhooks.endpoints.length > 0);
    if (!hooks || hooks.setup_mode === 'none' || accounts.length === 0) {
        return null;
    }
    const help = setupHelp(hooks.setup_mode, connection.name, environment);
    const canInstall = canEdit && hooks.setup_mode === 'api' && hooks.install.available;
    const guide = guideFor(connection.system, hooks.setup_mode, hooks.install.available);
    return (
        <CardSection title="Webhooks" testId="webhooks">
            {help && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '68ch' }} data-testid="setup-help">
                    {help}
                    {hooks.auth_required ? ' Webhooks without the token are refused.' : ''}
                </Typography>
            )}
            {accounts.map((account) => (
                <AccountEndpoints
                    key={account.slug || '_default'}
                    connection={connection}
                    account={account}
                    titled={accounts.length > 1}
                    canInstall={canInstall && credentialsReady(account.credentials)}
                />
            ))}
            {guide && <SetupGuideBlock connection={connection} guide={guide} accounts={accounts} />}
        </CardSection>
    );
};

export default WebhookEndpoints;
